import React, { useEffect, useRef, useState } from 'react'
import { Upload, Image, Button, Space } from 'antd'
import {
  UploadOutlined,
  CameraOutlined,
  AimOutlined,
  DownloadOutlined,
  StarFilled,
  StarOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  DownOutlined,
} from '@ant-design/icons'

/**
 * Componente para manejar las fotos del material.
 * Muestra las fotos en un carrusel (visor principal + tira de miniaturas)
 * para soportar cualquier cantidad de imágenes en el mismo espacio.
 */
const PhotosCard = ({
  images,
  isReadOnly,
  basculaConectada,
  todasLasCotasGuardadas,
  imagenFavorita,
  onUpload,
  onDelete,
  onDownload,
  onTomarFoto,
  onVerCotasGuardadas,
  onSetImagenFavorita,
}) => {
  const imagenesConUrl = images.filter(img => img?.signed_url)
  const total = imagenesConUrl.length

  const [indiceActivo, setIndiceActivo] = useState(0)
  const [abierto, setAbierto] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const totalAnterior = useRef(total)
  const thumbsRef = useRef(null)

  // Al cargar las fotos se arranca en la favorita (o la primera); al agregar
  // fotos después se salta a la última; al eliminar se ajusta el índice.
  useEffect(() => {
    if (totalAnterior.current === 0 && total > 0) {
      const indiceFavorita = imagenesConUrl.findIndex(img => img.nombre_objeto === imagenFavorita)
      setIndiceActivo(Math.max(indiceFavorita, 0))
    } else if (total > totalAnterior.current) {
      setIndiceActivo(total - 1)
    } else if (indiceActivo > total - 1) {
      setIndiceActivo(Math.max(total - 1, 0))
    }
    totalAnterior.current = total
  }, [total])

  // Mantener visible la miniatura activa en la tira.
  useEffect(() => {
    const thumb = thumbsRef.current?.children?.[indiceActivo]
    thumb?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [indiceActivo])

  const irAnterior = () => setIndiceActivo(i => (i - 1 + total) % total)
  const irSiguiente = () => setIndiceActivo(i => (i + 1) % total)

  // Sube cada archivo seleccionado; se retorna false para que antd no intente
  // su propio POST (la subida la hace onUpload).
  const beforeUpload = (file) => {
    onUpload(file)
    return false
  }

  const imagenActiva = imagenesConUrl[indiceActivo]
  const esFavorita = (image) => imagenFavorita === image.nombre_objeto

  const renderVisor = () => {
    if (!imagenActiva) {
      return (
        <Upload
          listType="picture-card"
          showUploadList={false}
          beforeUpload={beforeUpload}
          multiple
          accept="image/*"
          className="compact-upload photos-carousel-empty"
          disabled={isReadOnly}
        >
          <div style={{ textAlign: 'center' }}>
            <UploadOutlined style={{ fontSize: 18, color: '#1890ff' }} />
            <div style={{ marginTop: 4, color: '#999', fontSize: '11px' }}>Subir fotos</div>
          </div>
        </Upload>
      )
    }

    const favorita = esFavorita(imagenActiva)

    return (
      <div className="photos-carousel-viewer">
        <button
          className={`favorite-image-button ${favorita ? 'is-favorite' : ''}`}
          type="button"
          onClick={() => {
            if (!favorita && !isReadOnly) {
              onSetImagenFavorita(imagenActiva.nombre_objeto)
            }
          }}
          disabled={favorita || isReadOnly}
          title={favorita ? 'Imagen favorita' : 'Marcar como favorita'}
        >
          {favorita ? <StarFilled /> : <StarOutlined />}
        </button>

        <Image
          src={imagenActiva.signed_url}
          alt={imagenActiva.nombre_objeto}
          preview={false}
          onClick={() => setPreviewVisible(true)}
        />
        <Image.PreviewGroup
          items={imagenesConUrl.map(img => img.signed_url)}
          preview={{
            visible: previewVisible,
            onVisibleChange: (visible) => setPreviewVisible(visible),
            current: indiceActivo,
            onChange: (current) => setIndiceActivo(current),
          }}
        />

        {total > 1 && (
          <>
            <button
              type="button"
              className="photos-carousel-arrow is-prev"
              onClick={irAnterior}
              title="Anterior"
            >
              <LeftOutlined />
            </button>
            <button
              type="button"
              className="photos-carousel-arrow is-next"
              onClick={irSiguiente}
              title="Siguiente"
            >
              <RightOutlined />
            </button>
          </>
        )}

        <span className="photos-carousel-counter">
          {indiceActivo + 1} / {total}
        </span>
      </div>
    )
  }

  return (
    <div className={`photos-card ${abierto ? '' : 'is-collapsed'}`}>
      <div className="photos-card-header">
        <button
          type="button"
          className="photos-card-toggle"
          onClick={() => setAbierto(a => !a)}
        >
          <DownOutlined className="photos-card-chevron" />
          {!abierto && imagenesConUrl[0] && (
            <img
              className="photos-card-mini"
              src={imagenesConUrl[0].signed_url}
              alt={imagenesConUrl[0].nombre_objeto}
            />
          )}
          <span className="photos-card-title">
            Fotos ({total})
          </span>
        </button>
        <Space>
          <Button
            type="primary"
            icon={<CameraOutlined />}
            onClick={() => {
              // La foto nueva entra al carrusel; se abre para verla.
              setAbierto(true)
              onTomarFoto()
            }}
            disabled={isReadOnly}
            size="small"
            style={{
              backgroundColor: basculaConectada ? '#52c41a' : '#d9d9d9',
              borderColor: basculaConectada ? '#52c41a' : '#d9d9d9',
            }}
            title={basculaConectada ? 'Tomar foto con cámara' : 'Socket no conectado'}
          >
            Cámara
          </Button>
          <Button
            type="primary"
            icon={<AimOutlined />}
            onClick={onVerCotasGuardadas}
            disabled={isReadOnly || todasLasCotasGuardadas.length === 0}
            size="small"
            style={{
              backgroundColor: todasLasCotasGuardadas.length > 0 ? '#1890ff' : '#d9d9d9',
              borderColor: todasLasCotasGuardadas.length > 0 ? '#1890ff' : '#d9d9d9',
            }}
            title={todasLasCotasGuardadas.length > 0 ? `Ver medidas guardadas (${todasLasCotasGuardadas.length} disponible${todasLasCotasGuardadas.length > 1 ? 's' : ''})` : 'No hay medidas guardadas'}
          >
            Medidas {todasLasCotasGuardadas.length > 0 && `(${todasLasCotasGuardadas.length})`}
          </Button>
        </Space>
      </div>
      {abierto && (
      <div className="photos-card-body photos-carousel">
        {renderVisor()}

        {imagenActiva && (
          <div className="image-actions-container">
            <button
              className="download-image-button"
              type="button"
              onClick={() => onDownload(imagenActiva.signed_url, imagenActiva.nombre_objeto)}
              title="Descargar imagen 1000×1000 (ecommerce)"
            >
              <DownloadOutlined />
            </button>
            {!isReadOnly && (
              <button
                className="delete-image-button"
                type="button"
                onClick={() => onDelete(imagenActiva.nombre_objeto)}
              >
                Eliminar
              </button>
            )}
          </div>
        )}

        {total > 0 && (
          <div className="photos-carousel-thumbs-row">
            <div className="photos-carousel-thumbs" ref={thumbsRef}>
              {imagenesConUrl.map((img, index) => (
                <button
                  key={img.nombre_objeto}
                  type="button"
                  className={`photos-carousel-thumb ${index === indiceActivo ? 'is-active' : ''}`}
                  onClick={() => setIndiceActivo(index)}
                  title={img.nombre_objeto}
                >
                  <img src={img.signed_url} alt={img.nombre_objeto} />
                  {esFavorita(img) && <StarFilled className="photos-carousel-thumb-star" />}
                </button>
              ))}
            </div>
            {!isReadOnly && (
              <Upload
                showUploadList={false}
                beforeUpload={beforeUpload}
                multiple
                accept="image/*"
              >
                <button
                  type="button"
                  className="photos-carousel-add"
                  title="Agregar fotos"
                >
                  <PlusOutlined />
                </button>
              </Upload>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  )
}

export default PhotosCard
